/**
 * /vaccines/compare/uk-vs-us  AND  /vaccines/compare/:a-vs-:b
 *
 * Two modes:
 *  1. UK-vs-US country comparison (no specific vaccines selected) — shows
 *     the full schedule from each country side-by-side.
 *  2. Any-two-vaccine comparison (uses /api/vaccine-comparison) — shows
 *     two single-vaccine records aligned by section.
 *
 * Reusable: this same comparison pattern is what future explorers
 * (Medicines, Chemicals) will lift verbatim.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Syringe, GitCompare, ChevronRight } from "lucide-react";
import api from "../../lib/api";
import SEO from "../../components/SEO";
import SaveButton from "../../components/research/SaveButton";
import CiteAndPrint from "../../components/research/CiteAndPrint";

function CountryComparison() {
    const [uk, setUk] = useState({ rows: [] });
    const [us, setUs] = useState({ rows: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/schedules/by-country/UK").then((r) => r.data).catch(() => ({ rows: [] })),
            api.get("/schedules/by-country/US").then((r) => r.data).catch(() => ({ rows: [] })),
        ]).then(([u, s]) => { setUk(u); setUs(s); }).finally(() => setLoading(false));
    }, []);

    // Cluster by vaccine_slug, then row out the country-specific schedules.
    const matrix = useMemo(() => {
        const all = new Map(); // slug -> { display_name, uk: [], us: [] }
        for (const r of uk.rows || []) {
            const e = all.get(r.vaccine_slug) || { display_name: r.vaccine_display_name, uk: [], us: [] };
            e.uk.push(r);
            all.set(r.vaccine_slug, e);
        }
        for (const r of us.rows || []) {
            const e = all.get(r.vaccine_slug) || { display_name: r.vaccine_display_name, uk: [], us: [] };
            e.us.push(r);
            all.set(r.vaccine_slug, e);
        }
        return [...all.entries()].map(([slug, v]) => ({ slug, ...v })).sort((a, b) => a.display_name.localeCompare(b.display_name));
    }, [uk, us]);

    if (loading) return <div className="text-[13px] text-[#64748B] py-10 text-center">Loading…</div>;

    return (
        <div className="bg-[#111827] border border-[#243044] rounded-xl overflow-hidden" data-testid="country-comparison">
            <div className="grid grid-cols-[1fr_1fr_1fr] text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] bg-[#0B1220] border-b border-[#243044]">
                <div className="px-5 py-3 font-semibold">Vaccine</div>
                <div className="px-5 py-3 font-semibold border-l border-[#243044]"><span className="text-[#56CCF2]">UK</span> · NHS</div>
                <div className="px-5 py-3 font-semibold border-l border-[#243044]"><span className="text-[#56CCF2]">US</span> · CDC</div>
            </div>
            {matrix.map((row) => (
                <Link
                    key={row.slug}
                    to={`/vaccines/v/${row.slug}`}
                    className="grid grid-cols-[1fr_1fr_1fr] text-sm text-white border-t border-[#243044] hover:bg-[#0B1220] transition-colors"
                    data-testid={`compare-row-${row.slug}`}
                >
                    <div className="px-5 py-3 flex items-center gap-2">
                        <Syringe className="w-3.5 h-3.5 text-[#56CCF2]" />
                        {row.display_name}
                    </div>
                    <div className="px-5 py-3 border-l border-[#243044] text-[12px]">
                        {row.uk.length === 0 ? <span className="text-[#475569] italic">Not on schedule</span> : row.uk.map((s) => <div key={s.id}>{s.age_label} <span className="text-[#64748B]">· dose {s.dose_number}/{s.total_doses}</span></div>)}
                    </div>
                    <div className="px-5 py-3 border-l border-[#243044] text-[12px]">
                        {row.us.length === 0 ? <span className="text-[#475569] italic">Not on schedule</span> : row.us.map((s) => <div key={s.id}>{s.age_label} <span className="text-[#64748B]">· dose {s.dose_number}/{s.total_doses}</span></div>)}
                    </div>
                </Link>
            ))}
        </div>
    );
}

function VaccinePairComparison({ a, b }) {
    const [bundle, setBundle] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/vaccine-comparison?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`)
            .then((r) => setBundle(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Comparison not available."));
    }, [a, b]);

    if (error) return <div className="bg-[#111827] border border-[#243044] rounded-xl p-6 text-[13px] text-[#F87171]">{error}</div>;
    if (!bundle) return <div className="text-[13px] text-[#64748B] py-10 text-center">Loading…</div>;
    const va = bundle.a.vaccine;
    const vb = bundle.b.vaccine;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="vaccine-pair-comparison">
            {[bundle.a, bundle.b].map((side, i) => {
                const v = side.vaccine;
                return (
                    <article key={v.slug} className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-1">Vaccine {i === 0 ? "A" : "B"}</div>
                                <Link to={`/vaccines/v/${v.slug}`} className="block text-lg font-semibold text-white hover:text-[#56CCF2] transition-colors">{v.display_name}</Link>
                                {(v.aka || []).length ? <div className="text-[11px] text-[#64748B] mt-0.5">{v.aka.join(" · ")}</div> : null}
                            </div>
                            <SaveButton kind="vaccine" refSlug={v.slug} refTitle={v.display_name} variant="icon" />
                        </div>
                        <Section title="Schedule">
                            {side.schedules.length === 0 ? (<em className="text-[#64748B]">No schedule rows.</em>) : (
                                <ul className="space-y-0.5 text-[12px]">
                                    {side.schedules.map((s) => (<li key={s.id}><span className="text-[#56CCF2]">{s.country}</span> · {s.age_label} · dose {s.dose_number}/{s.total_doses}</li>))}
                                </ul>
                            )}
                        </Section>
                        <Section title="Antigens">
                            {(v.antigens || []).length ? v.antigens.join(", ") : <em className="text-[#64748B]">Not documented.</em>}
                        </Section>
                        <Section title="Evidence blocks">
                            {side.evidence_block_count} published
                        </Section>
                    </article>
                );
            })}
            <div className="md:col-span-2 mt-2">
                <SaveButton kind="vaccine_comparison" refSlug={`${va.slug}-vs-${vb.slug}`} refTitle={`${va.display_name} vs ${vb.display_name}`} refUrl={`/vaccines/compare/${va.slug}-vs-${vb.slug}`} variant="pill" />
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mt-3 pt-3 border-t border-[#243044]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] mb-1.5">{title}</div>
            <div className="text-[13px] text-[#CBD5E1]">{children}</div>
        </div>
    );
}

export default function ComparisonPage() {
    const { pair } = useParams();

    // /vaccines/compare/uk-vs-us is country comparison.
    const isCountry = pair === "uk-vs-us" || pair === "us-vs-uk";
    let pairSlugs = null;
    if (!isCountry && pair && pair.includes("-vs-")) {
        const m = pair.match(/^(.+)-vs-(.+)$/);
        if (m) pairSlugs = { a: m[1], b: m[2] };
    }

    const title = isCountry
        ? "UK vs US childhood vaccination schedules"
        : pairSlugs
            ? `${pairSlugs.a} vs ${pairSlugs.b} — vaccine comparison`
            : "Compare vaccines";

    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1]" data-testid="comparison-page">
            <SEO title={title} canonicalPath={`/vaccines/compare/${pair || "uk-vs-us"}`} description={title} />
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />

            <nav className="max-w-6xl mx-auto px-6 pt-6 text-[11px] text-[#64748B]" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-[#56CCF2]">Library</Link>
                <ChevronRight className="w-3 h-3 inline mx-1.5" />
                <Link to="/vaccines" className="hover:text-[#56CCF2]">Vaccines</Link>
                <ChevronRight className="w-3 h-3 inline mx-1.5" />
                <span className="text-[#94A3B8]">Compare</span>
            </nav>

            <header className="max-w-6xl mx-auto px-6 pt-8 pb-10">
                <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-2.5">
                            <GitCompare className="w-3.5 h-3.5" />
                            Comparison
                        </div>
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-4">{title}</h1>
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed max-w-3xl">
                            {isCountry
                                ? "Side-by-side view of the UK and US routine childhood schedules. Click any vaccine to open its full research record."
                                : pairSlugs
                                    ? "Two vaccines aligned by schedule, antigens and published evidence. Click either name to open the full research record."
                                    : "Pick two vaccines to compare."}
                        </p>
                    </div>
                    {isCountry ? (
                        <div className="shrink-0">
                            <SaveButton kind="vaccine_comparison" refSlug="uk-vs-us" refTitle="UK vs US schedules" refUrl="/vaccines/compare/uk-vs-us" variant="pill" />
                        </div>
                    ) : null}
                </div>
                <div className="mt-6">
                    <CiteAndPrint
                        title={title}
                        canonicalPath={isCountry ? "/vaccines/compare/uk-vs-us" : `/vaccines/compare/${pairSlugs?.a}-vs-${pairSlugs?.b}`}
                        slug={isCountry ? "uk-vs-us" : `${pairSlugs?.a}-vs-${pairSlugs?.b}`}
                    />
                </div>
            </header>

            <section className="max-w-6xl mx-auto px-6 pb-16">
                {isCountry ? <CountryComparison /> : pairSlugs ? <VaccinePairComparison a={pairSlugs.a} b={pairSlugs.b} /> : (
                    <div className="text-[13px] text-[#64748B]">Provide a pair of vaccine slugs in the URL, e.g. <code className="text-[#56CCF2]">/vaccines/compare/mmr-vs-6-in-1</code></div>
                )}
            </section>
        </div>
    );
}
